import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";

import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { faSparkles, faXmark } from "@fortawesome/pro-solid-svg-icons";

import { faCircleCheck, faCirclePlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonStandard, Chip, Icon, LogoLoader, Text } from "@performyard/py-components";
import type { PreMeetingPrepResponseStreamJson } from "@performyard/service-contracts";
import { formatDateAsUTCString } from "@performyard/utils-isomorphic";

import { InsightRenderer } from "../../../../components/AIModules/SidePanel/RenderInsight";
import IconButton from "../../../../components/Button/IconButton";
import { CopyToClipboard } from "../../../../components/CopyToClipboard";
import IconComponent from "../../../../components/Icon/Icon";
import Panel from "../../../../components/Panel";

import { useSuppressAskPerformYard } from "../../../../containers/AskPerformYard/hooks/useSuppressAskPerformYard";

import type { PreMeetingPrepGoalItem } from "../types";

import { useAddMeetingAgendaItem } from "../../../../api/hooks";
import type { usePreMeetingPrep } from "../hooks/usePreMeetingPrep";

import { appendCurrentLocationAsFrom } from "../../../../utils/breadcrumbOrigin";
import "../utils/chart-setup";
import { formatMeetingDateTimeDisplay } from "../utils/dateFormatters";
import { formatPrepAsPlainText, isNoDataPlaceholder } from "../utils/formatPrepAsPlainText";
import { getProgressChartData, getProgressChartOptions } from "../utils/goal-progress-chart";

import GoalConstants from "../../../../constants/Goals";
import { PRE_MEETING_PREP_CONFIG } from "../config";
import { useMeetingContext } from "../context";
import { meetingDetailsQueryKeys } from "../query-keys";

type PreMeetingPrepPanelProps = ReturnType<typeof usePreMeetingPrep>;

/**
 * "Add to Agenda" text button matching Figma design:
 * [+ icon] Add to Agenda in primary blue, right-aligned
 */
const AddToAgendaButton = ({ onClick, isAdded }: { onClick: () => void; isAdded: boolean }) => {
	return (
		<button
			type="button"
			onClick={isAdded ? undefined : onClick}
			disabled={isAdded}
			className={`flex items-center gap-1 self-end border-none bg-transparent p-0 outline-none ${isAdded ? "cursor-default" : "cursor-pointer hover:opacity-80"}`}
		>
			<Icon icon={isAdded ? faCircleCheck : faCirclePlus} size={16} color={isAdded ? "green-5" : "primary-5"} />
			<Text size="paragraph-3" weight="semi-bold" color={isAdded ? "green-5" : "primary-5"}>
				{isAdded ? "Added" : "Add to Agenda"}
			</Text>
		</button>
	);
};

/**
 * Goal card matching MeetingGoalListItem pattern:
 * Doughnut chart + blue link name + assignee/due date + status chip + Add to Agenda
 */
const GoalItemCard = ({
	goal,
	meetingId,
	meetingName,
	onAdded,
	isAdded,
}: {
	goal: PreMeetingPrepGoalItem;
	meetingId: string;
	meetingName?: string;
	onAdded: (goalId: string) => void;
	isAdded: boolean;
}) => {
	const { mutate: addAgendaItem } = useAddMeetingAgendaItem();
	const queryClient = useQueryClient();

	const handleAdd = useCallback(() => {
		addAgendaItem(
			{
				data: {
					name: goal.goalName,
					meetings: [{ meetingId, status: "incomplete" }],
					goalId: goal.goalId,
				},
			},
			{
				onSuccess: async () => {
					await queryClient.invalidateQueries({
						queryKey: meetingDetailsQueryKeys.meetingAgendaItems(meetingId),
					});
					onAdded(goal.goalId);
				},
			}
		);
	}, [addAgendaItem, goal, meetingId, onAdded, queryClient]);

	const formattedDueDate = goal.dueDate ? formatDateAsUTCString(new Date(goal.dueDate)) : "";

	return (
		<div className="flex w-full flex-col gap-1">
			<div className="box-border flex min-w-0 max-w-full items-start gap-2 rounded-md border border-solid border-neutral-3 bg-neutral-1 p-3">
				<div className="h-8 w-8 flex-shrink-0">
					<Doughnut
						width={32}
						height={32}
						options={getProgressChartOptions()}
						data={getProgressChartData(goal.progress || 0)}
					/>
				</div>
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<a
						href={appendCurrentLocationAsFrom(`/goal/${goal.goalId}/`, meetingName)}
						className="text-primary-5 no-underline hover:underline"
					>
						<Text size="paragraph-3" weight="semi-bold" color="primary-5">
							{goal.goalName}
						</Text>
					</a>
					<div className="flex flex-wrap items-center gap-1">
						{goal.assigneeName && (
							<Text size="caption" weight="regular">
								{goal.assigneeName}
							</Text>
						)}
						{goal.assigneeName && formattedDueDate && (
							<Text size="caption" weight="regular">
								•
							</Text>
						)}
						{formattedDueDate && (
							<Text size="caption" weight="regular">
								Due: {formattedDueDate}
							</Text>
						)}
					</div>
					<div className="w-fit">
						<Chip
							label={`Status: ${GoalConstants.statusLabels?.[goal.status as keyof typeof GoalConstants.statusLabels] || goal.status}`}
							color="neutral"
							size={20}
						/>
					</div>
				</div>
			</div>
			<AddToAgendaButton onClick={handleAdd} isAdded={isAdded} />
		</div>
	);
};

/**
 * Suggested agenda item card matching Figma: bordered card with topic + reason + Add to Agenda
 */
const SuggestedAgendaItemCard = ({
	itemKey,
	topic,
	reason,
	meetingId,
	onAdded,
	isAdded,
}: {
	itemKey: string;
	topic: string;
	reason: string;
	meetingId: string;
	onAdded: (key: string) => void;
	isAdded: boolean;
}) => {
	const { mutate: addAgendaItem } = useAddMeetingAgendaItem();
	const queryClient = useQueryClient();

	const handleAdd = useCallback(() => {
		addAgendaItem(
			{
				data: {
					name: topic,
					meetings: [{ meetingId, status: "incomplete" }],
				},
			},
			{
				onSuccess: async () => {
					await queryClient.invalidateQueries({
						queryKey: meetingDetailsQueryKeys.meetingAgendaItems(meetingId),
					});
					onAdded(itemKey);
				},
			}
		);
	}, [addAgendaItem, topic, meetingId, onAdded, queryClient, itemKey]);

	return (
		<div className="flex w-full flex-col gap-1">
			<div className="box-border flex min-w-0 max-w-full flex-col gap-2 rounded-md border border-solid border-neutral-3 bg-neutral-1 p-3">
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					{topic}
				</Text>
				<Text size="caption" weight="regular">
					{reason}
				</Text>
			</div>
			<AddToAgendaButton onClick={handleAdd} isAdded={isAdded} />
		</div>
	);
};

/**
 * Panel header: PerformYard branding + close button + meeting name card.
 * Shared between loading, content, and error states per Figma.
 */
const PanelHeader = ({
	meetingName,
	meetingDate,
	onClose,
	showMeetingCard = true,
}: {
	meetingName?: string;
	meetingDate?: string;
	onClose: () => void;
	showMeetingCard?: boolean;
}) => (
	<div className="flex flex-col gap-1 px-4 pt-4">
		<div className="flex w-full items-center justify-between">
			<div className="flex items-center gap-1 rounded p-1">
				<IconComponent icon={faSparkles} color="neutral-9" size="regular" />
				<Text size="paragraph-2" weight="regular" color="neutral-9">
					PerformYard
				</Text>
			</div>
			<IconButton
				testId="pre-meeting-prep-close-button"
				icon={faXmark}
				onClick={onClose}
				aria-label="Close panel"
			/>
		</div>
		{showMeetingCard && meetingName && (
			<div className="border-b border-solid border-neutral-2 pb-4">
				<div className="flex flex-col gap-2 rounded-md bg-neutral-1 px-4 py-2">
					<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
						{meetingName}
					</Text>
					{meetingDate && (
						<Text size="paragraph-3" weight="regular" color="neutral-9">
							<Text size="paragraph-3" weight="semi-bold" color="neutral-9" overrideElement="span">
								Meeting Date:
							</Text>{" "}
							{meetingDate}
						</Text>
					)}
				</div>
			</div>
		)}
	</div>
);

const PreMeetingPrepPanel = ({
	data,
	goalsData,
	isLoading,
	error,
	isOpen,
	handleClose,
	handleRegenerate,
	aiCoachingSettings,
}: PreMeetingPrepPanelProps): React.ReactElement => {
	const { meeting } = useMeetingContext();
	const [addedItems, setAddedItems] = useState<Set<string>>(new Set());

	// Hide the Ask PerformYard FAB while the panel is open so it doesn't overlap the footer actions.
	useSuppressAskPerformYard(isOpen);

	const formattedMeetingDate = useMemo(() => {
		if (!meeting?.startDateTime || !meeting?.endDateTime) return undefined;
		const { dateStr, timeStr, isMultiDay } = formatMeetingDateTimeDisplay(
			new Date(meeting.startDateTime),
			new Date(meeting.endDateTime)
		);
		return isMultiDay ? dateStr : `${dateStr} ${timeStr}`;
	}, [meeting?.startDateTime, meeting?.endDateTime]);

	const handleItemAdded = useCallback((key: string) => {
		setAddedItems((prev) => new Set(prev).add(key));
	}, []);

	const prepData = data?.data as PreMeetingPrepResponseStreamJson | undefined;

	// Build top insight sections (meeting context + recap) rendered before interactive sections
	const topInsight = useMemo(() => {
		if (!prepData) return null;
		const insight: Record<string, unknown> = {};

		if (prepData.executiveSummary) {
			insight.executiveSummary = prepData.executiveSummary;
		}
		if (prepData.lastThreeMeetingsRecap?.length) {
			insight.lastThreeMeetingsRecap = prepData.lastThreeMeetingsRecap;
		}

		return Object.keys(insight).length > 0 ? insight : null;
	}, [prepData]);

	// Build bottom insight sections (feedback, reviews, surveys) rendered after interactive sections
	const bottomInsight = useMemo(() => {
		if (!prepData) return null;
		const insight: Record<string, unknown> = {};

		if (prepData.recentFeedback?.length) {
			const filtered = prepData.recentFeedback.filter((item) => !isNoDataPlaceholder(item));
			if (filtered.length) {
				insight.recentFeedback = filtered;
			}
		}
		if (prepData.recentReviews && !isNoDataPlaceholder(prepData.recentReviews)) {
			insight.recentReviews = prepData.recentReviews;
		}
		if (prepData.surveyInsights && !isNoDataPlaceholder(prepData.surveyInsights)) {
			insight.surveyInsights = prepData.surveyInsights;
		}

		return Object.keys(insight).length > 0 ? insight : null;
	}, [prepData]);

	const clipboardText = useMemo(() => {
		if (!prepData) return "";
		return formatPrepAsPlainText(prepData, {
			overdue: goalsData.overdue,
			upcomingDueDate: goalsData.upcomingDueDate,
			outstandingUpdates: goalsData.outstandingUpdates,
		});
	}, [prepData, goalsData]);

	const hasGoals =
		goalsData.overdue.length > 0 || goalsData.upcomingDueDate.length > 0 || goalsData.outstandingUpdates.length > 0;

	return (
		<Panel side="right" isOpen={isOpen} onClose={handleClose} width={416}>
			{error ? (
				/* ===== ERROR STATE (Figma: header + centered error + footer with Retry/Cancel) ===== */
				<div className="flex h-full flex-col">
					<PanelHeader onClose={handleClose} showMeetingCard={false} />
					<div className="flex flex-1 flex-col items-center justify-center whitespace-pre-wrap px-4 text-center">
						<div className="mb-2 aspect-square w-fit rounded-full bg-red-1 p-1">
							<Icon icon={faExclamationCircle} color="red-5" size={24} />
						</div>
						<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
							{error.message || "The generation of Pre-Meeting Prep was unsuccessful. Please try again."}
						</Text>
					</div>
					<div className="flex items-center gap-2 border-t border-solid border-neutral-2 px-4 py-4">
						<div className="flex h-8 flex-1 items-center">
							<ButtonStandard variant="solid" color="primary" size="medium" onClick={handleRegenerate}>
								Retry
							</ButtonStandard>
						</div>
						<div className="flex h-8 flex-1 items-center">
							<ButtonStandard variant="outline" color="neutral" size="medium" onClick={handleClose}>
								Cancel
							</ButtonStandard>
						</div>
					</div>
				</div>
			) : isLoading ? (
				/* ===== LOADING STATE (Figma: header + meeting card + centered loader) ===== */
				<div className="flex h-full flex-col">
					<PanelHeader meetingName={meeting?.name} meetingDate={formattedMeetingDate} onClose={handleClose} />
					<div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
						<LogoLoader
							textArray={PRE_MEETING_PREP_CONFIG.loadingMessages}
							mainText="Generating Meeting Prep"
							switchTimer={2500}
						/>
					</div>
				</div>
			) : prepData ? (
				/* ===== CONTENT STATE ===== */
				<div className="flex h-full flex-col gap-4">
					{/* Sticky Header */}
					<div className="sticky top-0 z-10 bg-white">
						<PanelHeader
							meetingName={meeting?.name}
							meetingDate={formattedMeetingDate}
							onClose={handleClose}
						/>
					</div>

					{/* Scrollable Content */}
					<div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
						{/* Info section */}
						<Text size="caption" weight="regular" color="neutral-9">
							Pre-Meeting Prep Populated:{" "}
							{data?.createdAt ? formatDateAsUTCString(new Date(data.createdAt)) : ""}
						</Text>

						{/* Title */}
						<div className="flex items-center gap-1">
							<IconComponent icon={faSparkles} color="primary-5" size="small" />
							<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
								Pre-Meeting Prep
							</Text>
						</div>

						{/* Meeting Context + Recap from Last 3 Meetings */}
						{topInsight && (
							<InsightRenderer insight={topInsight} titleMap={PRE_MEETING_PREP_CONFIG.sectionTitleMap} />
						)}

						{/* Suggested Agenda Items */}
						{prepData.suggestedAgendaItems && prepData.suggestedAgendaItems.length > 0 && (
							<div className="flex flex-col gap-4">
								<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
									Suggested Agenda Items:
								</Text>
								<div className="flex min-w-0 flex-col gap-4 rounded-md bg-neutral-1 px-4 py-3">
									{prepData.suggestedAgendaItems.map((item, index) => {
										const itemKey = `${item.topic}-${index}`;
										return (
											<SuggestedAgendaItemCard
												key={itemKey}
												itemKey={itemKey}
												topic={item.topic}
												reason={item.reason}
												meetingId={meeting._id}
												onAdded={handleItemAdded}
												isAdded={addedItems.has(itemKey)}
											/>
										);
									})}
								</div>
							</div>
						)}

						{/* Goals for Discussion (overdue > at risk > upcoming) */}
						{aiCoachingSettings?.goalsForDiscussion && hasGoals && (
							<div className="flex flex-col gap-4">
								<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
									Goals for Discussion:
								</Text>
								<div className="flex min-w-0 flex-col gap-4 rounded-md bg-neutral-1 px-4 py-3">
									{goalsData.overdue.length > 0 && (
										<>
											<Text size="paragraph-3" weight="semi-bold" color="neutral-7">
												Overdue Goals
											</Text>
											<div className="flex flex-col gap-2">
												{goalsData.overdue.map((goal) => (
													<GoalItemCard
														key={goal.goalId}
														goal={goal}
														meetingId={meeting._id}
														meetingName={meeting.name}
														onAdded={handleItemAdded}
														isAdded={addedItems.has(goal.goalId)}
													/>
												))}
											</div>
										</>
									)}
									{goalsData.outstandingUpdates.length > 0 && (
										<>
											<Text size="paragraph-3" weight="semi-bold" color="neutral-7">
												At Risk Goals:
											</Text>
											<div className="flex flex-col gap-2">
												{goalsData.outstandingUpdates.map((goal) => (
													<GoalItemCard
														key={goal.goalId}
														goal={goal}
														meetingId={meeting._id}
														meetingName={meeting.name}
														onAdded={handleItemAdded}
														isAdded={addedItems.has(goal.goalId)}
													/>
												))}
											</div>
										</>
									)}
									{goalsData.upcomingDueDate.length > 0 && (
										<>
											<Text size="paragraph-3" weight="semi-bold" color="neutral-7">
												Upcoming Goals:
											</Text>
											<div className="flex flex-col gap-2">
												{goalsData.upcomingDueDate.map((goal) => (
													<GoalItemCard
														key={goal.goalId}
														goal={goal}
														meetingId={meeting._id}
														meetingName={meeting.name}
														onAdded={handleItemAdded}
														isAdded={addedItems.has(goal.goalId)}
													/>
												))}
											</div>
										</>
									)}
								</div>
							</div>
						)}

						{/* Recent Feedback, Recent Reviews, Survey Insights */}
						{bottomInsight && (
							<InsightRenderer
								insight={bottomInsight}
								titleMap={PRE_MEETING_PREP_CONFIG.sectionTitleMap}
							/>
						)}
					</div>

					{/* Footer */}
					<div className="sticky bottom-0 flex items-center gap-2 border-t border-solid border-neutral-2 bg-white px-4 py-4">
						<div className="flex h-8 flex-1 items-center">
							<ButtonStandard
								variant="solid"
								color="primary"
								size="medium"
								iconLeft={faSparkles}
								onClick={handleRegenerate}
								disabled={isLoading}
							>
								Regenerate
							</ButtonStandard>
						</div>
						{isLoading ? (
							<div className="flex h-8 flex-1 items-center">
								<ButtonStandard variant="outline" color="neutral" size="medium" disabled>
									Copy to Clipboard
								</ButtonStandard>
							</div>
						) : (
							<CopyToClipboard text={clipboardText}>
								<div className="flex h-8 flex-1 items-center">
									<ButtonStandard variant="outline" color="neutral" size="medium">
										Copy to Clipboard
									</ButtonStandard>
								</div>
							</CopyToClipboard>
						)}
					</div>
				</div>
			) : null}
		</Panel>
	);
};

export default PreMeetingPrepPanel;
